/**
 * TriggerEffectManager - Processes trigger effects during gameplay
 *
 * Handles events like on_hit, on_kill, on_take_damage and executes
 * associated effects from items, passives, and skill stones.
 */

import {
  TriggerEffect,
  TriggerType,
  TriggerEffectAction,
  StatCalculator,
} from './StatCalculator';

// ========================
// Trigger Event Data
// ========================

export interface TriggerEventData {
  // Common
  sourceEntityId?: string;
  targetEntityId?: string;

  // Damage related
  damageDealt?: number;
  damageType?: string;
  isCritical?: boolean;

  // Position
  positionX?: number;
  positionY?: number;

  // Target info
  targetMaxHp?: number;
  targetCurrentHp?: number;

  // Skill info
  skillId?: string;
  skillElement?: string;
  skillBaseType?: string;

  // Attacker/Defender current state
  attackerHpPercent?: number;
  defenderHpPercent?: number;
}

// ========================
// Effect Result
// ========================

export interface TriggerEffectResult {
  effectId: string;
  effectName: string;
  action: TriggerEffectAction;
  triggered: boolean;
  cooldownRemaining?: number;
}

// ========================
// Main Manager Class
// ========================

export class TriggerEffectManager {
  private cooldowns: Map<string, number> = new Map();

  /**
   * Update cooldowns
   */
  update(deltaTime: number): void {
    for (const [effectId, remaining] of this.cooldowns.entries()) {
      const newRemaining = remaining - deltaTime;
      if (newRemaining <= 0) {
        this.cooldowns.delete(effectId);
      } else {
        this.cooldowns.set(effectId, newRemaining);
      }
    }
  }

  /**
   * Process a trigger event and return effects that should activate
   */
  processTrigger(
    triggerType: TriggerType,
    calculator: StatCalculator,
    eventData: TriggerEventData
  ): TriggerEffectResult[] {
    const effects = calculator.getTriggerEffects(triggerType);
    const results: TriggerEffectResult[] = [];

    for (const effect of effects) {
      const result = this.tryTriggerEffect(effect, eventData);
      results.push(result);
    }

    return results.filter(r => r.triggered);
  }

  /**
   * Try to trigger a single effect
   */
  private tryTriggerEffect(
    effect: TriggerEffect,
    eventData: TriggerEventData
  ): TriggerEffectResult {
    // Check cooldown
    const cooldownRemaining = this.cooldowns.get(effect.id);
    if (cooldownRemaining && cooldownRemaining > 0) {
      return {
        effectId: effect.id,
        effectName: effect.name,
        action: effect.effect,
        triggered: false,
        cooldownRemaining,
      };
    }

    // Check conditions
    if (effect.condition) {
      if (!this.checkCondition(effect.condition, eventData)) {
        return {
          effectId: effect.id,
          effectName: effect.name,
          action: effect.effect,
          triggered: false,
        };
      }
    }

    // Set cooldown if applicable
    if (effect.condition?.cooldown) {
      this.cooldowns.set(effect.id, effect.condition.cooldown);
    }

    return {
      effectId: effect.id,
      effectName: effect.name,
      action: effect.effect,
      triggered: true,
    };
  }

  /**
   * Check if conditions are met
   */
  private checkCondition(
    condition: NonNullable<TriggerEffect['condition']>,
    eventData: TriggerEventData
  ): boolean {
    // Chance check
    if (condition.chance !== undefined) {
      if (Math.random() * 100 > condition.chance) {
        return false;
      }
    }

    // HP threshold check
    if (condition.hpThreshold !== undefined) {
      if (eventData.attackerHpPercent === undefined ||
          eventData.attackerHpPercent > condition.hpThreshold) {
        return false;
      }
    }

    // Mana threshold check
    // (would need mana in event data)

    // Element type check
    if (condition.elementType !== undefined) {
      if (eventData.damageType !== condition.elementType &&
          eventData.skillElement !== condition.elementType) {
        return false;
      }
    }

    // Skill type check
    if (condition.skillType !== undefined) {
      if (eventData.skillBaseType !== condition.skillType) {
        return false;
      }
    }

    return true;
  }

  /**
   * Clear all cooldowns
   */
  clearCooldowns(): void {
    this.cooldowns.clear();
  }

  /**
   * Get remaining cooldown for an effect
   */
  getCooldown(effectId: string): number {
    return this.cooldowns.get(effectId) || 0;
  }
}

// ========================
// Singleton Instance
// ========================

let triggerManagerInstance: TriggerEffectManager | null = null;

export function getTriggerEffectManager(): TriggerEffectManager {
  if (!triggerManagerInstance) {
    triggerManagerInstance = new TriggerEffectManager();
  }
  return triggerManagerInstance;
}

// ========================
// Helper Functions
// ========================

/**
 * Apply effect result to game state
 * Returns actions for the game world to process
 */
export interface EffectApplicationResult {
  healAmount?: number;
  manaAmount?: number;
  damageToApply?: { amount: number; element: string; aoe?: number; x: number; y: number };
  statusToApply?: { status: string; duration: number; stacks?: number };
  statusToRemove?: string;
  spawnEffect?: string;
  modifierToApply?: { modifier: any; duration?: number };
}

export function resolveEffectAction(
  result: TriggerEffectResult,
  damageDealt: number,
  position: { x: number; y: number }
): EffectApplicationResult {
  const resolved: EffectApplicationResult = {};
  const action = result.action;

  switch (action.type) {
    case 'heal':
      resolved.healAmount = Math.floor(action.isPercent
        ? damageDealt * (action.amount / 100)
        : action.amount);
      break;

    case 'restore_mana':
      resolved.manaAmount = Math.floor(action.isPercent
        ? damageDealt * (action.amount / 100)
        : action.amount);
      break;

    case 'deal_damage':
      resolved.damageToApply = {
        amount: action.damage,
        element: action.element,
        aoe: action.aoe,
        x: position.x,
        y: position.y,
      };
      break;

    case 'apply_status':
      resolved.statusToApply = {
        status: action.status,
        duration: action.duration,
        stacks: action.stacks,
      };
      break;

    case 'remove_status':
      resolved.statusToRemove = action.status;
      break;

    case 'spawn_effect':
      resolved.spawnEffect = action.effectId;
      break;

    case 'apply_modifier':
      resolved.modifierToApply = {
        modifier: action.modifier,
        duration: action.duration,
      };
      break;

    case 'leech':
      const leechAmount = Math.floor(damageDealt * (action.percent / 100));
      if (action.stat === 'hp') {
        resolved.healAmount = leechAmount;
      } else {
        resolved.manaAmount = leechAmount;
      }
      break;
  }

  return resolved;
}
